import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex, Text } from "@chakra-ui/react";
import { Plus, EllipsisVertical, Pencil, Trash2, Eye } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";
import ViewResourceDrawer from "./components/ViewResourceDrawer";
import AddResourceDrawer from "./components/AddResourceDrawer";
import EditResourceDrawer from "./components/EditResourceDrawer";
import DeleteResourceModal from "./components/DeleteResourceModal";


import { useDispatch, useSelector } from "react-redux";
import {
    fetchResourcesAction,
    createResourceAction,
    updateResourceAction,
    deleteResourceAction,
} from "state/actions/resourceActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";

const ManageResourcesTab = () => {
    const dispatch = useDispatch();
    const notify = useCustomNotification();

    const { resources, loading } = useSelector((state) => state.resources);

    const [isAddOpen, setAddOpen] = useState(false);
    const [isEditOpen, setEditOpen] = useState(false);
    const [isDeleteOpen, setDeleteOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [isViewOpen, setViewOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchResourcesAction());
    }, [dispatch]);


    const handleCreate = async (formData) => {
        try {
            await dispatch(createResourceAction(formData));
            setAddOpen(false);

            notify({
                type: "success",
                message: "Resource Created",
                description: "Resource uploaded successfully.",
            });
        } catch (err) {
            notify({
                type: "error",
                message: "Error",
                description: "Failed to create resource.",
            });
        }
    };


    const handleUpdate = async (data) => {
        try {
            await dispatch(updateResourceAction(data.resource_id, data.title));
            setEditOpen(false);

            notify({
                type: "success",
                message: "Resource Updated",
                description: "Resource updated successfully.",
            });
        } catch (err) {
            notify({
                type: "error",
                message: "Error",
                description: "Failed to update resource.",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await dispatch(deleteResourceAction(selected.resource_id));
            setDeleteOpen(false);

            notify({
                type: "success",
                message: "Resource Deleted",
                description: "Resource deleted successfully.",
            });
        } catch (err) {
            notify({
                type: "error",
                message: "Error",
                description: "Failed to delete resource.",
            });
        }
    };

    const generateMenuItems = (row) => [
        {
            icon: <Eye size={16} />,
            text: "View",
            onClick: () => {
                setSelected(row.original);
                setViewOpen(true);
            },
        },
        {
            icon: <Pencil size={16} />,
            text: "Edit",
            onClick: () => {
                setSelected(row.original);
                setEditOpen(true);
            },
        },
        {
            icon: <Trash2 size={16} />,
            text: "Delete",
            onClick: () => {
                setSelected(row.original);
                setDeleteOpen(true);
            },
        },
    ];

    const columnsData = [
        {
            Header: "Title",
            accessor: "title",
        },
        {
            Header: "Files",
            accessor: "documents",
            Cell: ({ value }) => (
                <Text>{value?.length || 0} file(s)</Text>
            ),
        },
        {
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }) => (
                <ThreeDotMenu
                    icon={<EllipsisVertical size={20} color="grey" />}
                    items={generateMenuItems(row)}
                />
            ),
        },
    ];

    return (
        <Box>
            <Flex justify="flex-end" mb={4}>
                {loading ? (
                    <Skeleton width="120px" height="40px" />
                ) : (
                    <GradientButton
                        icon={<Plus size={16} />}
                        onClick={() => setAddOpen(true)}
                    >
                        Add Resource
                    </GradientButton>
                )}
            </Flex>

            <ReusableTable
                columnsData={columnsData}
                tableData={resources || []}
                isLoading={loading}
            />
            <AddResourceDrawer
                isOpen={isAddOpen}
                onClose={() => setAddOpen(false)}
                onSubmit={handleCreate}
            />
            <ViewResourceDrawer
                isOpen={isViewOpen}
                onClose={() => setViewOpen(false)}
                resource={selected}
            />

            <EditResourceDrawer
                isOpen={isEditOpen}
                onClose={() => setEditOpen(false)}
                resource={selected}
                onSubmit={handleUpdate}
            />

            <DeleteResourceModal
                isOpen={isDeleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDelete}
            />
        </Box>
    );
};

export default ManageResourcesTab;